import { useRouter } from "next/router";
import Link from "next/link";
import React from "react";
import { createMarkup } from "../../../../utils/helper";
import { formatScore } from "../../../../utils/wine";
import LockedContentWrapper from "../../Wrappers/LockedContentWrapper";
import { Wine } from "../../../../context/wineContext/reducer";
import TastingNote from "./TastingNote";
import { useAuth } from "../../../../context/authContext";
import { Skeleton } from "../../Skeleton/Skeleton";

type WineReviewProps = {
  showVintage?: boolean,
  review?: Wine & {
    drinking_window: string;
    article_slug: string;
    article_title: string;
    tasted_on?: string;
    article_date?: string;
  },
  handleSelectedReviews: (wineId: number) => void;
};

const WineReview = ({ review, showVintage, handleSelectedReviews }: WineReviewProps) => {
  const router = useRouter();
  const {state: { user, loading }} = useAuth();
  const [loadingState, setLoadingState] = React.useState(true);
  const reviewData = review || ({} as NonNullable<WineReviewProps["review"]>);
  const {
    id: reviewId,
    vintage,
    score_raw = "",
    drinking_window,
    drinking_window_begin = 0,
    drinking_window_end,
    tasting_note,
    author,
    tasted_on,
    article_date,
    article_slug = "",
    article_title,
    producer_commentary
  } = reviewData;
  const [isCheckboxChecked, setIsCheckboxChecked] = React.useState(false);
  const [mounted, setMounted] = React.useState(false);

  const bylineWithDate = React.useMemo(() => {
    if (tasted_on) {
      return tasted_on;
    } else {
      return article_date;
    }
  }, [tasted_on, article_date]);

  const redirectToSignIn = () => {
    router.push({pathname: "/users/sign_in", query: { from: router.asPath}})
  }

  React.useEffect(() => {
    if (mounted && !loading) {
      setLoadingState(false);
    }
  }, [mounted, loading]);

  React.useEffect(() => {
    setMounted(true);
  } , []);

  return loadingState ? (
    <div className="flex items-center no-print flex-row space-x-1 justify-stretch bg-gray-100 p-2">
      <Skeleton
        className="w-1/4 hidden sm:block ml-4 pl-6"
        lines={3}
        titleLineStyle="h-4 w-10 p-6 ml-4"
        linesStyle="w-16 ml-2"
      />
      <Skeleton className="w-full" lineHeight="2" titleLineStyle="h-4 mt-4" />
    </div>
  ) : (
    <div className="p-4 bg-gray-100 rounded flex flex-col space-y-2 md:space-y-0 md:flex-row">
      <div className="mt-10 mr-6">
        <input
          type="checkbox"
          className="h-4 w-4 text-red focus:ring-red border-gray-300 rounded no-print"
          id="checkbox"
          checked={isCheckboxChecked}
          onChange={() => {
            setIsCheckboxChecked(!isCheckboxChecked)
            if (typeof reviewId === "number") {
              handleSelectedReviews(reviewId)
            }
          }}
          aria-label="Select this wine review"
        />
      </div>
      <div className="w-full md:w-1/5 md:mr-4 space-y-1 lg:space-y-0">
        <div className="wine-score-paywall">
          <LockedContentWrapper
            message={
              <div className="w-full hidden md:block flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-14 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
            }
            placeholderContent={
              <p className={`font-bold text-red text-5xl opacity-50`}>
                00
              </p>
            }
          >
            {/* score_raw is null for non-subscribers; children render even while locked */}
            <p className={`font-bold text-red ${ (score_raw || "").includes("(") ? "text-3xl" : "text-5xl" }`}>
              { formatScore(score_raw) }
            </p>
          </LockedContentWrapper>
        </div>

        {
          showVintage && vintage &&
            <div className="hidden md:block pb-2">
              <div className="text-sm">
                Vintage
              </div>
              <p className="font-light text-red">
                { vintage }
              </p>
            </div>
        }

        {
          drinking_window_begin > 0 &&
          <div className="md:block">
            <div className="text-sm">
              Drinking Window
            </div>
            <p className="font-light">
              { drinking_window_begin } - { drinking_window_end }
            </p>
          </div>
        }
      </div>

      <div className="w-full">
        <div className="wine-review-paywall">
          <LockedContentWrapper
            message={
              <div className="space-y-4 text-center">
                <div className="flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <p className="pt-1 pl-3 text-2xl">
                    Subscriber Access Only
                  </p>
                </div>
                {!user ? (
                  <p className="text-sm">
                    <button
                      onClick={() => redirectToSignIn()}
                      className="text-red"
                    >
                      Log In
                    </button>{" "}
                    or{" "}
                    <Link
                      href="/users/plan"
                      className="text-red"
                    >
                      Sign Up
                    </Link>
                  </p>
                ) : (
                  <p className="text-sm">
                    <Link href="/users/edit" className="text-red">
                      Renew or Subscribe
                    </Link>{" "}
                    to access this content{" "}
                  </p>
                )}
              </div>
            }
            // TODO: find out what to do with this -- it is using lorem ipsum
            placeholderContent={
              <div>
                <p className="italic text-sm text-red pb-2"><Link href="/articles">You&#39;ll Find The Article Name Here</Link></p>
                <p className="pb-2">
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer vitae aliquam odio. Aliquam purus diam, tempor et consectetur vitae, eleifend ac quam. Proin nec mauris ac odio iaculis semper. Integer posuere pharetra aliquet. Nullam tincidunt sagittis est in maximus. Donec sem orci, vulputate ac quam non, consectetur fermentum diam. In dignissim magna id orci dignissim convallis. Integer sit amet placerat dui. Aliquam pharetra ornare nulla at vulputate. Sed dictum, mi eget fringilla lacinia, nisl tortor condimentum mi, vitae ultrices quam diam ac neque. Donec hendrerit vulputate felis, fringilla varius massa.
                </p>
                <p className="opacity-40 italic text-sm">- By Author Name, Month Date, Year</p>
              </div>
            }
          >
            {
              tasting_note && <>
                {
                  article_slug && article_slug.length > 0 &&
                  <p className="italic text-sm text-red pb-2">From: <Link href={ `/articles/` + article_slug }>{ article_title }</Link></p>
                }
                <TastingNote renderByline review={reviewData} />
              </>
            }
          </LockedContentWrapper>
        </div>

        {
          mounted && producer_commentary &&
            <div
              className="font-light text-sm border-t pt-4 pb-2 mt-4 [&>p]:mb-4 [&>p:last-child]:mb-0"
              dangerouslySetInnerHTML={createMarkup(producer_commentary)}
            />
        }
        {
          drinking_window &&
          <div className="block md:hidden">
            <div className="text-sm">
              Drinking Window
            </div>
            <p className="font-light">
              { drinking_window }
            </p>
          </div>
        }
      </div>
    </div>
  )
}

export default WineReview;
